import { GridCellProps } from '@progress/kendo-react-grid';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import GridCellBase from 'src/components/GridCellBase';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Alert from 'src/components/design-system/Alert';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import {
  CanAccessReceipts,
  CreateInvoicePermission,
  ThreeWayMatchingPermissions,
} from 'src/components/guards/PermissionsList';
import usePermission from 'src/hooks/usePermissions';
import useReceiptDetails from 'src/hooks/useReceiptDetails';
import useRedirect from 'src/hooks/useRedirect';
import { InvoiceContext } from 'src/sections/invoice_details/InvoiceDetailsContext';
import useUnLinkInvoice from 'src/services/useUnLinkInvoice';
import { isEqualString } from 'src/utils/strings';
import { useContextSelector } from 'use-context-selector';
import { PoRelatedDocumentsContext } from '../PoRelatedDocumentsContext';

const LinkedDocument = (props: GridCellProps) => {
  const poDetails = useContextSelector(PoRelatedDocumentsContext, (value) => value.poDetails);
  const refresh = useContextSelector(PoRelatedDocumentsContext, (value) => value.refresh);

  const { dataItem } = props;
  const [searchParams] = useSearchParams();
  const isInvoiceEditDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceEditDisabled
  );
  const invoiceRefresh = useContextSelector(InvoiceContext, (value) => value.refreshPage);

  const { refreshPage: receiptRefresh } = useReceiptDetails();
  const { getUrl, redirect } = useRedirect();

  const receiptAccess = usePermission({
    permissions: CanAccessReceipts,
    or: true,
    clientId: dataItem?.buyerId,
  });

  const invoiceAccess = usePermission({
    permissions: CreateInvoicePermission,
    or: true,
    clientId: dataItem?.buyerId,
  });

  const isThreeWayMatchingEnabled = usePermission({ permissions: ThreeWayMatchingPermissions });
  const linkEnabled = useMemo(
    () => (dataItem?.documentType !== 'Receipt' ? receiptAccess : invoiceAccess),
    [dataItem?.documentType, invoiceAccess, receiptAccess]
  );
  const receiptId = useMemo(() => searchParams.get('recid'), [searchParams]);

  const {
    isReceipt,
    receiptIdToUnlink,
    invoiceIdToUnlink,
    isLinkedCurrent,
    value,
    isDocumentInProgress,
    isPoClosed,
    showAlert,
    isDocumentDeleted,
  } = useMemo(() => {
    const isCurrent = dataItem?.documentId?.toString() === receiptId;
    const isLinkedCurrent = dataItem?.linkedDocumentId?.toString() === receiptId;
    const isReceipt = dataItem?.documentType === 'Receipt';
    const isInvoice = !isReceipt;
    const receiptIdToUnlink = isReceipt ? dataItem?.documentId : dataItem?.linkedDocumentId;
    const invoiceIdToUnlink = isInvoice ? dataItem?.documentId : dataItem?.linkedDocumentId;
    const value = dataItem?.linkedDocumentName;
    const isDocumentInProgress =
      dataItem?.status === 'In Progress' || dataItem?.status === 'In Progress (Action Required)';
    const isDocumentComplete = isEqualString(dataItem?.status, 'completed');
    const isPoClosed = isEqualString(poDetails?.poStatus, 'closed');
    const showAlert = isCurrent && isInvoice && isThreeWayMatchingEnabled;
    const isDocumentDeleted = isEqualString(dataItem?.status, 'deleted');

    return {
      isCurrent,
      isReceipt,
      isInvoice,
      receiptIdToUnlink,
      invoiceIdToUnlink,
      isLinkedCurrent,
      value,
      isDocumentInProgress,
      isDocumentComplete,
      isPoClosed,
      showAlert,
      isDocumentDeleted,
    };
  }, [
    dataItem?.documentId,
    dataItem?.documentType,
    dataItem?.linkedDocumentId,
    dataItem?.linkedDocumentName,
    dataItem?.status,
    isThreeWayMatchingEnabled,
    poDetails?.poStatus,
    receiptId,
  ]);

  const { unlinkInvoice, isLoading } = useUnLinkInvoice(invoiceIdToUnlink, receiptIdToUnlink);
  const handleUnLink = useCallback(async () => {
    try {
      await unlinkInvoice();
      toast.success('Document unlinked successfully!');
    } catch (error) {
      console.error(error);
    } finally {
      invoiceRefresh?.();
      receiptRefresh?.();
      await refresh();
    }
  }, [invoiceRefresh, receiptRefresh, refresh, unlinkInvoice]);

  const handleClick = useCallback(() => {
    if (!isLinkedCurrent) {
      redirect(
        getUrl(isReceipt ? 'invoiceDetails' : 'receiptDetails')(
          dataItem.linkedDocumentId,
          dataItem.poId
        )
      );
    }
  }, [dataItem.linkedDocumentId, dataItem.poId, getUrl, isLinkedCurrent, isReceipt, redirect]);

  const messages: string[] = useMemo(() => {
    let message: string[] = [];
    if (!isDocumentInProgress) {
      message = ['Current Document is not in progress.'];
    } else if (isPoClosed) {
      message = ['Current PO is closed.'];
    } else if (isDocumentDeleted) {
      message = ['Document needs to be undeleted to unlink.'];
    } else if (isInvoiceEditDisabled) {
      message = ['Editing is disabled for this document.'];
    }
    return message;
  }, [isDocumentDeleted, isDocumentInProgress, isInvoiceEditDisabled, isPoClosed]);

  return (
    <GridCellBase title={value} {...props} rowSelectionEnabled={true}>
      {dataItem.linkedDocumentId ? (
        <div className="flex flex-row gap-1 justify-between">
          <button
            onClick={handleClick}
            className={
              linkEnabled ? (isLinkedCurrent ? '' : 'text-selected underline cursor-pointer') : ''
            }
          >
            {value || dataItem.linkedDocumentId || 'Unassigned'}
          </button>
          {isDocumentInProgress ? (
            <PopUp
              anchorAlign={{ horizontal: 'right', vertical: 'top' }}
              popupAlign={{ horizontal: 'right', vertical: 'bottom' }}
              content={<PopUpContentWrapper type="Error" contents={messages} />}
              show={!isDocumentInProgress || isPoClosed || !!isInvoiceEditDisabled}
            >
              <Button
                color="info"
                disabled={!isDocumentInProgress || isPoClosed || !!isInvoiceEditDisabled}
                loading={isLoading}
                onClick={handleUnLink}
                size="md"
              >
                Unlink
              </Button>
            </PopUp>
          ) : (
            ''
          )}
        </div>
      ) : showAlert ? (
        <Alert variant="error">You must link a completed receipt</Alert>
      ) : (
        ''
      )}
    </GridCellBase>
  );
};

export default LinkedDocument;
